import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { useEnrollment } from '@/hooks/useEnrollments'
import { useFinalGrade, usePracticalScores, useCalculateFinalGrade, useQuizScores, useGradingScheme } from '@/hooks/useGrading'
import { useOverallProgress } from '@/hooks/useProgress'
import { useStudentAssignmentSubmissions } from '@/hooks/useAssignments'
import { GradeDisplay } from '@/components/grading/GradeDisplay'
import { GradingBreakdown } from '@/components/grading/GradingBreakdown'

export const StudentGradePage: React.FC = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>()
  const id = enrollmentId || ''

  const { data: enrollmentResponse, isLoading: loadingEnrollment } = useEnrollment(id)
  const enrollment = enrollmentResponse?.data

  const { data: finalGradeData, isLoading: loadingGrade } = useFinalGrade(id)
  const { data: practicalScoresData, isLoading: loadingPractical } = usePracticalScores(id)
  const { data: progressData, isLoading: loadingProgress } = useOverallProgress(id)
  const { data: assignmentSubmissions, isLoading: loadingAssignments } = useStudentAssignmentSubmissions(id)

  const { data: quizScores } = useQuizScores(id)

  const { data: schemeData } = useGradingScheme(enrollment?.courseId || '')

  const calculateGrade = useCalculateFinalGrade()

  const practicalScores = Array.isArray(practicalScoresData)
    ? practicalScoresData
    : (practicalScoresData as any)?.data || []

  const finalGrade = (finalGradeData as any)?.data || finalGradeData

  const avgPractical =
    practicalScores.length > 0
      ? practicalScores.reduce((sum: number, s: any) => sum + s.score, 0) / practicalScores.length
      : null

  const breakdownScores: Record<string, number> = {}
  if (avgPractical !== null) {
    breakdownScores['practical'] = avgPractical
  }
  if (quizScores && quizScores.length > 0) {
    const completedQuizzes = quizScores.filter((qs: any) => qs.status !== 'needs_review')
    if (completedQuizzes.length > 0) {
      breakdownScores['quiz'] = completedQuizzes.reduce((sum: number, qs: any) => sum + qs.score, 0) / completedQuizzes.length
    }
  }
  if (assignmentSubmissions && assignmentSubmissions.length > 0) {
    const gradedSubs = assignmentSubmissions.filter((sub: any) => sub.status === 'graded' && sub.score != null)
    if (gradedSubs.length > 0) {
      breakdownScores['assignment'] = gradedSubs.reduce((sum: number, sub: any) => sum + sub.score, 0) / gradedSubs.length
    }
  }

  const isLoading = loadingEnrollment || loadingGrade || loadingPractical || loadingProgress || loadingAssignments

  const handleCalculate = async () => {
    if (!id) return
    try {
      await calculateGrade.mutateAsync(id)
    } catch {
      // error is surfaced in UI via mutation state
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
        <div className="animate-spin rounded-full size-10 border-4 border-primary/30 border-t-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark px-4 py-8 max-w-2xl mx-auto">
      {/* Back link */}
      <Link
        to={`/learn/${id}`}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-text-muted hover:text-primary transition-colors mb-6"
      >
        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
        Back to classroom
      </Link>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Grades</h1>
        {enrollment?.course && (
          <p className="text-sm text-gray-500 dark:text-text-muted mt-1">
            {enrollment.course.title}
          </p>
        )}
      </div>

      {/* Final Grade Card */}
      <div className="rounded-2xl bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6 mb-4">
        <h2 className="text-sm font-bold text-gray-500 dark:text-text-muted uppercase tracking-wide mb-4">
          Final Grade
        </h2>

        {finalGrade ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <GradeDisplay grade={finalGrade.grade} size="lg" />
              <div>
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {finalGrade.totalScore.toFixed(1)}
                  <span className="text-base font-normal text-gray-500 dark:text-text-muted">/100</span>
                </span>
                <p className="text-sm text-gray-500 dark:text-text-muted mt-1">
                  Calculated at {new Date(finalGrade.calculatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div
              className={`px-4 py-2 rounded-xl text-sm font-bold ${
                finalGrade.isPassed
                  ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                  : 'bg-red-500/20 text-red-600 dark:text-red-400'
              }`}
            >
              {finalGrade.isPassed ? 'Passed' : 'Not Passed'}
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="text-sm text-gray-500 dark:text-text-muted">
              Your final grade has not been calculated yet.
            </p>
            <button
              onClick={handleCalculate}
              disabled={calculateGrade.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-bold hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 whitespace-nowrap"
            >
              {calculateGrade.isPending ? (
                <>
                  <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-[18px]">calculate</span>
                  Calculate Grade
                </>
              )}
            </button>
          </div>
        )}

        {calculateGrade.isError && (
          <p className="mt-3 text-sm text-red-500">
            {(calculateGrade.error as any)?.message || 'Failed to calculate grade.'}
          </p>
        )}
      </div>

      {/* Grading Breakdown */}
      {schemeData && (
        <div className="mb-4">
          <GradingBreakdown scheme={schemeData} scores={breakdownScores} />
        </div>
      )}

      {/* Quiz Progress Card */}
      <div className="rounded-2xl bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6 mb-4">
        <h2 className="text-sm font-bold text-gray-500 dark:text-text-muted uppercase tracking-wide mb-4">
          Quiz Progress
        </h2>

        {progressData ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 dark:text-white font-medium">Overall progress</span>
              <span className="font-bold text-gray-900 dark:text-white">
                {(progressData as any).percentage ?? (progressData as any).completionPercentage ?? 0}%
              </span>
            </div>
            <div className="w-full bg-gray-100 dark:bg-white/10 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${
                    (progressData as any).percentage ??
                    (progressData as any).completionPercentage ??
                    0
                  }%`,
                }}
              />
            </div>
            {(progressData as any).completed !== undefined && (
              <p className="text-xs text-gray-500 dark:text-text-muted">
                {(progressData as any).completed} of {(progressData as any).total} lessons completed
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-text-muted">No progress data available.</p>
        )}
      </div>

      {/* Quiz Scores */}
      {quizScores && quizScores.length > 0 && (
        <div className="bg-white dark:bg-surface-card rounded-2xl border border-gray-200 dark:border-white/5 p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-primary">quiz</span>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Nilai Kuis</h3>
          </div>
          <div className="space-y-3">
            {quizScores.map((qs: any) => (
              <div key={qs.quizId} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{qs.quizTitle}</p>
                  <p className="text-xs text-gray-500 dark:text-text-muted mt-0.5">
                    {qs.status === 'needs_review' ? 'Menunggu Review Essay' : 'Selesai'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {qs.status === 'needs_review' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
                      <span className="material-symbols-outlined text-[12px]">pending</span>
                      Menunggu
                    </span>
                  ) : (
                    <>
                      <span className={`text-sm font-bold ${qs.isPassed ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {qs.score}/100
                      </span>
                      <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        qs.isPassed
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                      }`}>
                        {qs.isPassed ? 'Lulus' : 'Gagal'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {quizScores && quizScores.length === 0 && (
        <div className="bg-white dark:bg-surface-card rounded-2xl border border-gray-200 dark:border-white/5 p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-primary">quiz</span>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Nilai Kuis</h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-text-muted">Belum ada nilai kuis</p>
        </div>
      )}

      {/* Practical Scores Card */}
      <div className="rounded-2xl bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6">
        <h2 className="text-sm font-bold text-gray-500 dark:text-text-muted uppercase tracking-wide mb-4">
          Practical Scores
        </h2>

        {practicalScores.length > 0 ? (
          <div className="space-y-3">
            {practicalScores.map((score: any, index: number) => (
              <div
                key={score.id || index}
                className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-surface-dark border border-gray-100 dark:border-white/5"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Assessment #{index + 1}
                  </p>
                  {score.notes && (
                    <p className="text-xs text-gray-500 dark:text-text-muted mt-0.5">{score.notes}</p>
                  )}
                  <p className="text-xs text-gray-400 dark:text-text-muted mt-0.5">
                    {score.scoredAt ? new Date(score.scoredAt).toLocaleDateString() : ''}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-gray-900 dark:text-white">
                    {score.score}
                    <span className="text-sm font-normal text-gray-400">/100</span>
                  </span>
                </div>
              </div>
            ))}

            {avgPractical !== null && (
              <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-white/5">
                <span className="text-sm font-medium text-gray-700 dark:text-text-muted">
                  Average practical score
                </span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {avgPractical.toFixed(1)}/100
                </span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-text-muted">
            No practical scores recorded yet.
          </p>
        )}
      </div>

      {/* Assignment Scores Card */}
      <div className="rounded-2xl bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6 mt-4">
        <h2 className="text-sm font-bold text-gray-500 dark:text-text-muted uppercase tracking-wide mb-4">
          Assignment Scores
        </h2>

        {assignmentSubmissions && assignmentSubmissions.length > 0 ? (
          <div className="space-y-3">
            {assignmentSubmissions
              .filter((sub: any) => sub.status === 'graded' && sub.score !== undefined && sub.score !== null)
              .map((sub: any, index: number) => (
                <div
                  key={sub.id || index}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-surface-dark border border-gray-100 dark:border-white/5"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Submission #{index + 1}
                    </p>
                    {sub.feedback && (
                      <p className="text-xs text-gray-500 dark:text-text-muted mt-0.5 line-clamp-1">{sub.feedback}</p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-text-muted mt-0.5">
                      {sub.gradedAt ? new Date(sub.gradedAt).toLocaleDateString() : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                      {sub.score}
                      <span className="text-sm font-normal text-gray-400">/100</span>
                    </span>
                  </div>
                </div>
              ))}
            {assignmentSubmissions.filter((sub: any) => sub.status === 'graded').length === 0 && (
              <p className="text-sm text-gray-500 dark:text-text-muted">
                No graded assignments yet.
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-text-muted">
            No assignment submissions yet.
          </p>
        )}
      </div>
    </div>
  )
}

export default StudentGradePage
